import { TEST_IDS } from '@va/constants';
import { fontWeights, Paragraph, paragraphSizes, ToggleSwitch } from '@va/ui/design-system';
import { TooltipWrapper } from '@va/ui/tooltips';
import { useCheckOverflow } from '@va/util/hooks';
import classNames from 'classnames';
import { useMemo, useRef } from 'react';

type FeatureToggleSectionProps = {
  /** Identifies the toggle in tests, rendered as `TEST_IDS.helpers.createToggleId(name)`. */
  name: string;
  tooltip: string;
  title: string;
  isToggleChecked: boolean;
  onToggleChange: () => void;
  disabled: boolean;
  disabledTooltip?: string;
  icon?: React.ReactNode;
  className?: string;
};

export function FeatureToggleSection({
  name,
  tooltip,
  title,
  isToggleChecked,
  onToggleChange,
  disabled,
  disabledTooltip,
  icon,
  className,
}: FeatureToggleSectionProps) {
  const titleRef = useRef(null);
  const isOverflow = useCheckOverflow(titleRef);

  const isTooltipDisabled = useMemo(() => {
    if (isOverflow || tooltip) return false;
    return true;
  }, [isOverflow, tooltip]);

  return (
    <div
      className={classNames(
        'flex flex-row items-center gap-3 bg-white-snow p-18 rounded-2xl min-w-0 flex-nowrap',
        {
          'cursor-not-allowed opacity-80': disabled,
          'cursor-default hover:bg-gray-concrete': !disabled,
        },
        className,
      )}
    >
      {icon}
      <TooltipWrapper
        content={
          <div>
            <p className='text-14 py-1 text-center'>{title}</p>
            {tooltip}
          </div>
        }
        interactive
        trigger='click'
        disabled={isTooltipDisabled}
      >
        <Paragraph
          ref={titleRef}
          weight={fontWeights.medium}
          size={paragraphSizes.normal}
          className={classNames({
            'border-b border-dashed border-gray-silver': tooltip,
          })}
          colorClassName='text-gray-charcoal truncate'
        >
          {title}
        </Paragraph>
      </TooltipWrapper>
      <TooltipWrapper content={disabledTooltip} disabled={!disabled || !disabledTooltip}>
        <div className='ml-auto'>
          <ToggleSwitch
            onChange={onToggleChange}
            checked={isToggleChecked}
            disabled={disabled}
            data-testid={TEST_IDS.helpers.createToggleId(name)}
          />
        </div>
      </TooltipWrapper>
    </div>
  );
}
